// Promoted from cx3-frontend/lib/suite.ts, which is the origin of this list
// and keeps its own copy for its in-app "Open CMSS" CTAs (app/(dashboard)/home/page.tsx).
// This is the shared source going forward; cx3-frontend's copy is not
// re-imported from here yet because there is no publish/pin step for this
// package (see README) — keep the two in sync by hand until that lands.
export interface SuiteApp {
  id: string;
  name: string;
  tagline: string;
  icon: string; // key into ICONS
  status: "active" | "connected" | "available";
  accent: string; // CSS color, used for the icon tile background
  launchUrl: string;
}

// CX3's own routes below (cx3, cyble, cyboard, cybloop, cypilot, cyshield,
// cybright, integrations) are all absolute, pointing at CX3's own origin —
// not relative paths. This banner is rendered from other apps' origins too
// (cmss-client), and a relative "/home" would silently navigate *within
// whichever app the banner is currently mounted on* instead of jumping to
// CX3 (e.g. clicking "CX3" from CMSS landed on cmss-client's own 404 at
// localhost:3010/home). Clicking your own current-app tile is a no-op
// (guarded in suite-banner.ts), so this is always safe even when already on CX3.
//
// SSO-SEC-014: these two used to be hardcoded consts, so every non-local
// deployment (Server B, Server C) shipped a banner that could only ever
// navigate to the viewer's own machine. Now overridable per host app via the
// <cybears-suite-banner cx3-origin="..." cmss-launch-url="..."> attributes
// (see suite-banner.ts) -- each host app fills these in from its own env
// config at build time. Defaults stay localhost so local dev needs no config.
const DEFAULT_CX3_ORIGIN = "http://localhost:3000";
const DEFAULT_CMSS_LAUNCH_URL = "http://localhost:3010/login?sso=auto";
// XAwareness (2026-10-11): its sign-in page starts Login with Cybears at once
// with ?sso=auto. Set per host with the `xawareness-launch-url` attribute.
const DEFAULT_XAWARENESS_LAUNCH_URL = "https://xawareness.cybears.net/fr/auth/signin?sso=auto";

export function getSuiteApps(cx3Origin?: string, cmssLaunchUrl?: string, xawarenessLaunchUrl?: string): SuiteApp[] {
  const origin = cx3Origin || DEFAULT_CX3_ORIGIN;
  return [
    {
      id: "cx3",
      name: "CX3",
      tagline: "SecOps & XDR",
      icon: "shield",
      status: "active",
      accent: "linear-gradient(135deg,#7c3aed,#4f46e5)",
      // Its login page starts Login with Cybears at once with ?sso=auto
      // (2026-10-11), like XMSS's and XAwareness's: opening /home from
      // another product landed on a login page that waited for a click.
      launchUrl: `${origin}/login?sso=auto`,
    },
    {
      id: "cmss",
      // Shown as XMSS (the product's name since 2026-09-28); the id stays
      // "cmss" because host apps, launch attributes and links key on it.
      name: "XMSS",
      tagline: "Assessment",
      icon: "clipboard-check",
      status: "connected",
      accent: "linear-gradient(135deg,#2563eb,#0891b2)",
      // ?sso=auto tells its Login screen to kick off its own PKCE flow
      // immediately -- see launchSuiteApp() below for why this app can't
      // build that URL itself.
      launchUrl: cmssLaunchUrl || DEFAULT_CMSS_LAUNCH_URL,
    },
    {
      // Was the "Cyble" placeholder pointing at CX3's integration hub; the
      // product is XAwareness and signs in through Cybears SSO since
      // 2026-10-11, so the tile now opens it.
      id: "xawareness",
      name: "XAwareness",
      tagline: "Awareness",
      icon: "target",
      status: "connected",
      accent: "linear-gradient(135deg,#f59e0b,#ea580c)",
      launchUrl: xawarenessLaunchUrl || DEFAULT_XAWARENESS_LAUNCH_URL,
    },
    {
      id: "cyboard",
      name: "Cyboard",
      tagline: "GRC & Risk",
      icon: "scale",
      status: "available",
      accent: "linear-gradient(135deg,#059669,#0d9488)",
      launchUrl: `${origin}/apps/integration-hub`,
    },
    {
      id: "cybloop",
      name: "Cybloop",
      tagline: "Threat Intel",
      icon: "globe",
      status: "available",
      accent: "linear-gradient(135deg,#e11d48,#db2777)",
      launchUrl: `${origin}/apps/integration-hub`,
    },
    {
      id: "cypilot",
      name: "CyPilot",
      tagline: "AI Copilot",
      icon: "bot",
      status: "active",
      accent: "linear-gradient(135deg,#7c3aed,#c026d3)",
      launchUrl: `${origin}/apps/commandment-center`,
    },
    {
      id: "cyshield",
      name: "CyShield",
      tagline: "Perimeter",
      icon: "shield-check",
      status: "available",
      accent: "linear-gradient(135deg,#0891b2,#2563eb)",
      launchUrl: `${origin}/apps/integration-hub`,
    },
    {
      id: "cybright",
      name: "CyBright",
      tagline: "Academy",
      icon: "graduation-cap",
      status: "available",
      accent: "linear-gradient(135deg,#eab308,#d97706)",
      launchUrl: `${origin}/apps/integration-hub`,
    },
    {
      id: "integrations",
      name: "Hub",
      tagline: "Connectors",
      icon: "git-branch",
      status: "available",
      accent: "linear-gradient(135deg,#475569,#334155)",
      launchUrl: `${origin}/apps/integration-hub`,
    },
  ];
}

// Deliberately does NOT build a Keycloak authorize URL for any app here.
// PKCE's code_verifier is generated and later redeemed by whichever origin
// starts the flow, so a cross-app launch always hands off to that app's own
// login page and lets it run its own PKCE flow. See cx3-frontend/lib/suite.ts
// for the fuller version of this comment — the logic here is identical.
export function buildLaunchUrl(app: SuiteApp, currentAppId: string, tenantId?: string): string {
  let targetUrl = app.launchUrl;
  if (app.id === "cmss" && currentAppId === "cx3" && tenantId) {
    targetUrl += `&link_for=cx3&cx3_tenant_id=${encodeURIComponent(tenantId)}`;
  }
  return targetUrl;
}

export function launchSuiteApp(app: SuiteApp, currentAppId: string, tenantId?: string): void {
  const targetUrl = buildLaunchUrl(app, currentAppId, tenantId);
  if (targetUrl.startsWith("http")) {
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = targetUrl;
  }
}
